import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { EU_COUNTRIES, PRODUCT_CATEGORY_IDS, findCountry, getApplicableRate, parsePairSlug } from "../data/vatData";
import { fmtEUR, fmtPct } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { toast } from "sonner";
import {
    trackCalculatorUsed,
    trackCountryPair,
    trackShareLink,
} from "../lib/analytics";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Info, ArrowRight, Receipt, BadgeCheck, Calculator, Link2 } from "lucide-react";

// --------- URL state helpers ---------
function readInitialFromURL() {
    if (typeof window === "undefined") return {};
    const p = new URLSearchParams(window.location.search);
    const out = {};
    const net = p.get("net");
    const seller = p.get("seller");
    const buyer = p.get("buyer");
    const cat = p.get("cat");
    if (net != null && !Number.isNaN(parseFloat(net))) out.netPrice = String(parseFloat(net));
    if (seller && findCountry(seller.toUpperCase())) out.sellerCode = seller.toUpperCase();
    if (buyer && findCountry(buyer.toUpperCase())) out.buyerCode = buyer.toUpperCase();
    if (cat && PRODUCT_CATEGORY_IDS.includes(cat)) out.category = cat;
    return out;
}

function syncURL({ netPrice, sellerCode, buyerCode, category }) {
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    p.set("net", String(netPrice ?? ""));
    p.set("seller", sellerCode);
    p.set("buyer", buyerCode);
    p.set("cat", category);
    const newUrl = `${window.location.pathname}?${p.toString()}${window.location.hash || ""}`;
    window.history.replaceState({}, "", newUrl);
}

export default function VatCalculator() {
    const { t } = useI18n();
    const { pair: pairParam } = useParams();
    const pair = useMemo(() => parsePairSlug(pairParam), [pairParam]);
    const initial = useRef(readInitialFromURL()).current;

    const [netPrice, setNetPrice] = useState(initial.netPrice ?? "100");
    const [sellerCode, setSellerCode] = useState(initial.sellerCode ?? pair?.from?.code ?? "LV");
    const [buyerCode, setBuyerCode] = useState(initial.buyerCode ?? pair?.to?.code ?? "DE");
    const [category, setCategory] = useState(initial.category ?? "standard");

    // If user navigates between /vat/* routes, sync state to the new pair.
    useEffect(() => {
        if (pair?.from?.code) setSellerCode(pair.from.code);
        if (pair?.to?.code) setBuyerCode(pair.to.code);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pairParam]);

    const seller = useMemo(() => findCountry(sellerCode), [sellerCode]);
    const buyer = useMemo(() => findCountry(buyerCode), [buyerCode]);
    const applied = useMemo(() => getApplicableRate(buyer, category), [buyer, category]);

    const net = parseFloat(netPrice) || 0;
    const vatAmount = +(net * (applied.rate / 100)).toFixed(2);
    const gross = +(net + vatAmount).toFixed(2);

    const isCrossBorder = seller && buyer && seller.code !== buyer.code;
    const sellerEU = !!seller;

    // Keep URL in sync with state.
    useEffect(() => {
        syncURL({ netPrice, sellerCode, buyerCode, category });
    }, [netPrice, sellerCode, buyerCode, category]);

    // Analytics: debounce calculator_used (avoid one event per keystroke).
    useEffect(() => {
        const handle = setTimeout(() => {
            trackCalculatorUsed({
                seller: sellerCode,
                buyer: buyerCode,
                category,
                net,
                rate: applied.rate,
                gross,
            });
        }, 1000);
        return () => clearTimeout(handle);
    }, [netPrice, sellerCode, buyerCode, category, net, applied.rate, gross]);

    // Analytics: lane (country pair) changed
    const prevPair = useRef({ s: sellerCode, b: buyerCode });
    useEffect(() => {
        if (prevPair.current.s !== sellerCode || prevPair.current.b !== buyerCode) {
            trackCountryPair(sellerCode, buyerCode);
            prevPair.current = { s: sellerCode, b: buyerCode };
        }
    }, [sellerCode, buyerCode]);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            trackShareLink();
            toast.success(t("calc.copied"));
        } catch (e) {
            toast.error(t("calc.copyLink"));
        }
    };

    // Map applied.label key to i18n
    const appliedLabel = useMemo(() => {
        if (applied.label === "Standard") return t("rateLabels.standard");
        if (applied.label === "Reduced") return t("rateLabels.reduced");
        if (applied.label === "Standard (no reduced)") return t("rateLabels.standardNoReduced");
        return applied.label;
    }, [applied.label, t]);

    return (
        <section id="calculator" data-testid="vat-calculator-section" className="relative scroll-mt-20">
            <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 lg:pt-20 pb-10">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold tracking-wider uppercase text-slate-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />
                        {pair ? `${pair.from.flag} ${pair.from.name} → ${pair.to.flag} ${pair.to.name}` : t("hero.eyebrow")}
                    </div>
                    {pair ? (
                        <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-slate-900">
                            VAT from {pair.from.name}
                            <br className="hidden sm:block" />
                            {" "}to <span className="text-[#10B981]">{pair.to.name}</span>
                        </h1>
                    ) : (
                        <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-slate-900">
                            {t("hero.titleLine1")}
                            <span className="text-[#10B981]">{t("hero.titleHighlight")}</span>
                            <br className="hidden sm:block" />
                            {t("hero.titleLine2")}
                        </h1>
                    )}
                    <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-600 max-w-2xl">
                        {pair
                            ? `Sales from ${pair.from.flag} ${pair.from.name} to ${pair.to.flag} ${pair.to.name} — ${pair.to.name} VAT applies (${pair.to.standard}% standard) for B2C above the €10,000 OSS threshold. Below it you can keep applying ${pair.from.name} domestic VAT.`
                            : t("hero.subtitle")}
                    </p>
                </div>

                <div
                    data-testid="vat-calculator-card"
                    className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                >
                    {/* Inputs */}
                    <div className="lg:col-span-3 p-6 sm:p-8 border-b lg:border-b-0 lg:border-r border-slate-200">
                        <div className="flex items-center gap-2 mb-6">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-700">
                                <Calculator className="h-4 w-4" />
                            </span>
                            <h2 className="font-display text-xl font-bold text-slate-900">
                                {t("calc.transactionDetails")}
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="sm:col-span-2">
                                <Label htmlFor="net-price" className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                    {t("calc.netPrice")}
                                </Label>
                                <div className="mt-2 relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg font-display">€</span>
                                    <Input
                                        id="net-price"
                                        data-testid="net-price-input"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={netPrice}
                                        onChange={(e) => setNetPrice(e.target.value)}
                                        placeholder="100.00"
                                        className="h-14 pl-9 pr-4 text-2xl font-display font-bold text-slate-900 border-slate-200 focus-visible:ring-[#10B981] focus-visible:border-[#10B981]"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                    {t("calc.sellerCountry")}
                                </Label>
                                <Select value={sellerCode} onValueChange={setSellerCode}>
                                    <SelectTrigger
                                        data-testid="seller-country-select"
                                        className="h-12 mt-2 border-slate-200 text-slate-900 [&>span]:flex [&>span]:items-center [&>span]:gap-2"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72">
                                        {EU_COUNTRIES.map((c) => (
                                            <SelectItem key={c.code} value={c.code} data-testid={`seller-option-${c.code}`}>
                                                <span className="mr-2">{c.flag}</span> {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                    {t("calc.buyerCountry")}
                                </Label>
                                <Select value={buyerCode} onValueChange={setBuyerCode}>
                                    <SelectTrigger
                                        data-testid="buyer-country-select"
                                        className="h-12 mt-2 border-slate-200 text-slate-900 [&>span]:flex [&>span]:items-center [&>span]:gap-2"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72">
                                        {EU_COUNTRIES.map((c) => (
                                            <SelectItem key={c.code} value={c.code} data-testid={`buyer-option-${c.code}`}>
                                                <span className="mr-2">{c.flag}</span> {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="sm:col-span-2">
                                <Label className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                    {t("calc.productCategory")}
                                </Label>
                                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {PRODUCT_CATEGORY_IDS.map((id) => {
                                        const active = id === category;
                                        return (
                                            <button
                                                key={id}
                                                data-testid={`category-${id}`}
                                                type="button"
                                                onClick={() => setCategory(id)}
                                                className={`text-left p-3 rounded-md border transition-all ${
                                                    active
                                                        ? "border-[#10B981] bg-emerald-50 ring-1 ring-[#10B981]"
                                                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                                                }`}
                                            >
                                                <div className={`text-sm font-semibold ${active ? "text-emerald-700" : "text-slate-900"}`}>
                                                    {t(`categories.${id}.label`)}
                                                </div>
                                                <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                                                    {t(`categories.${id}.description`)}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Outputs */}
                    <div className="lg:col-span-2 p-6 sm:p-8 bg-slate-50/60">
                        <div className="flex items-center gap-2 mb-6">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#10B981]/10 text-[#10B981]">
                                <Receipt className="h-4 w-4" />
                            </span>
                            <h2 className="font-display text-xl font-bold text-slate-900">{t("calc.summary")}</h2>
                        </div>

                        <dl className="space-y-3">
                            <Row label={t("calc.vatRateApplied")} testId="output-vat-rate">
                                <span className="font-display font-bold text-slate-900">{fmtPct(applied.rate)}</span>
                                <span className="ml-2 text-xs text-slate-500">({appliedLabel})</span>
                            </Row>
                            <Row label={t("calc.vatAmount")} testId="output-vat-amount">
                                <span className="font-display font-bold text-slate-900">{fmtEUR(vatAmount)}</span>
                            </Row>
                            <Row label={t("calc.netPriceLabel")} testId="output-net-price">
                                <span className="font-mono text-slate-700">{fmtEUR(net)}</span>
                            </Row>
                        </dl>

                        <div className="mt-5 rounded-xl bg-white border border-[#10B981]/40 p-5 gross-pulse" data-testid="output-gross-card">
                            <div className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                {t("calc.grossPrice")}
                            </div>
                            <div className="mt-1 font-display text-3xl sm:text-4xl font-black text-[#10B981]" data-testid="output-gross-price">
                                {fmtEUR(gross)}
                            </div>
                            <div className="mt-1 text-xs text-slate-500 flex items-center gap-1.5">
                                <ArrowRight className="h-3 w-3" /> {buyer?.flag} {buyer?.name} · {t("calc.vatIncluded")}
                            </div>
                        </div>

                        {/* Copy shareable link */}
                        <button
                            data-testid="copy-link-btn"
                            onClick={handleCopyLink}
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                        >
                            <Link2 className="h-4 w-4 text-[#10B981]" />
                            {t("calc.copyLink")}
                        </button>
                        <p className="mt-1.5 text-[11px] text-slate-500 text-center">{t("calc.copyHint")}</p>

                        {/* Notices */}
                        <div className="mt-5 space-y-3">
                            {sellerEU && isCrossBorder && (
                                <Notice testId="oss-notice" tone="success" icon={<BadgeCheck className="h-4 w-4" />}>
                                    {t("calc.ossNotice", { seller: seller.name })}
                                </Notice>
                            )}
                            {sellerEU && isCrossBorder && (
                                <Notice testId="threshold-notice" tone="info" icon={<Info className="h-4 w-4" />}>
                                    {t("calc.thresholdNotice", { seller: seller.name, sellerFlag: seller.flag })}
                                </Notice>
                            )}
                            {sellerEU && !isCrossBorder && (
                                <Notice testId="domestic-notice" tone="info" icon={<Info className="h-4 w-4" />}>
                                    {t("calc.domesticNotice", { seller: seller.name, sellerFlag: seller.flag })}
                                </Notice>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Row({ label, children, testId }) {
    return (
        <div className="flex items-baseline justify-between border-b border-slate-200 pb-3" data-testid={testId}>
            <dt className="text-sm text-slate-600">{label}</dt>
            <dd className="text-right">{children}</dd>
        </div>
    );
}

function Notice({ children, tone = "info", icon, testId }) {
    const tones = {
        success: "bg-emerald-50 text-emerald-900 border-emerald-200",
        info: "bg-slate-100 text-slate-800 border-slate-200",
    };
    return (
        <div data-testid={testId} className={`flex gap-2.5 rounded-md border p-3 text-sm leading-relaxed ${tones[tone]}`}>
            <span className="mt-0.5 shrink-0">{icon}</span>
            <div>{children}</div>
        </div>
    );
}
